import React from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import type { AgentSpec } from '../../../types/backend';
import { useRetireAgent } from '../hooks/useAgentLifecycle';

interface RetireAgentDialogProps {
  open: boolean;
  agent: AgentSpec;
  /** The agents Studio holds, to find the active ones that consult this agent as a specialist. */
  specs: AgentSpec[];
  onClose: () => void;
}

/**
 * The retire confirmation (PS-UC-18). Retiring is allowed while rules and templates still name
 * the agent (2a), so it lists them and says their runs will fail before Retire can be pressed.
 * Afterwards it names the active agents that still call it as a specialist.
 */
const RetireAgentDialog: React.FC<RetireAgentDialogProps> = ({ open, agent, specs, onClose }) => {
  const retire = useRetireAgent(agent.agent_id, open);
  const rules = retire.references?.rules ?? [];
  const templates = retire.references?.templates ?? [];
  // PS-UC-18 2: the agents that list it as a specialist, read from the rows already held.
  const callers = specs.filter((s) => s.status === 'active' && s.callees.includes(agent.agent_id));
  const name = agent.display_name || agent.agent_id;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Retire {name}?</DialogTitle>
      <DialogContent>
        {retire.result ? (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>{name} is retired. Nothing dispatches to it again.</Typography>
            {retire.result.named_as_callee_by.length > 0 ? (
              <InlineAlert severity="warning">
                These active agents still list it as a specialist, and their calls to it will fail:{' '}
                {retire.result.named_as_callee_by.join(', ')}
              </InlineAlert>
            ) : (
              <Typography variant="body2" color="text.secondary">No active agent lists it as a specialist.</Typography>
            )}
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Its row, system prompt and history are kept, and its id is never reused.
            </Typography>
            {retire.referencesError ? (
              <InlineAlert severity="error">
                Could not read what names this agent: {retire.referencesError}
              </InlineAlert>
            ) : !retire.references ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
            ) : rules.length === 0 && templates.length === 0 && callers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No rule, template or agent names this agent.</Typography>
            ) : (
              <InlineAlert severity="warning">
                <Typography variant="body2" sx={{ mb: 1 }}>
                  These still name it, and their runs will fail once it is retired:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {rules.map((r) => (
                    <li key={`rule-${r.customer_id}-${r.rule_name}`}>
                      <Typography variant="body2">Rule {r.rule_name} ({r.customer_name}){r.enabled ? '' : ', disabled'}</Typography>
                    </li>
                  ))}
                  {callers.map((c) => (
                    <li key={`agent-${c.agent_id}`}>
                      <Typography variant="body2">Agent {c.agent_id} consults it as a specialist</Typography>
                    </li>
                  ))}
                  {templates.map((t) => (
                    <li key={`template-${t.template_id}`}>
                      <Typography variant="body2">Template {t.name} ({t.owner === 'global' ? 'global' : t.customer_id})</Typography>
                    </li>
                  ))}
                </Box>
              </InlineAlert>
            )}
            {retire.error && (
              <InlineAlert severity="error" sx={{ mt: 1.5 }}>{retire.error}</InlineAlert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {retire.result ? (
          <Button onClick={onClose} variant="contained">Close</Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              onClick={retire.retire}
              color="error"
              variant="contained"
              // A cached list that failed to refresh may be stale, so it does not count.
              disabled={!retire.references || !!retire.referencesError || retire.retiring}
            >
              Retire agent
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RetireAgentDialog;
